import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { createVM, getImages, getNodes, getVMs, getImageDescriptions } from './api';

// Permanent OrkaHub images available to all customers
const ORKAHUB_IMAGES = [
  {
    name: 'orkahub/sonoma',
    tag: 'latest',
    full_name: 'ghcr.io/macstadium/orka-images/sonoma:latest',
    isOrkaHub: true
  },
  {
    name: 'orkahub/sequoia',
    tag: 'latest',
    full_name: 'ghcr.io/macstadium/orka-images/sequoia:latest',
    isOrkaHub: true
  },
  {
    name: 'orkahub/tahoe',
    tag: 'latest',
    full_name: 'ghcr.io/macstadium/orka-images/tahoe:latest',
    isOrkaHub: true
  }
];

function CreateVMModal({ onClose, onSuccess, initialImage = '' }) {
  const [selectedImage, setSelectedImage] = useState(initialImage);
  // Default to a full Unity-friendly machine (≈ half a 128 GB / 28-core node, so 2 fit).
  const [cpu, setCpu] = useState('14');
  const [memory, setMemory] = useState('60');
  const [name, setName] = useState('');
  const [node, setNode] = useState('');
  const [showLatestOnly, setShowLatestOnly] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingMessage, setPollingMessage] = useState('');

  const { data: imagesData, isLoading: imagesLoading } = useQuery({
    queryKey: ['images'],
    queryFn: async () => {
      const response = await getImages();
      return response.data;
    },
  });

  const { data: nodesData, isLoading: nodesLoading } = useQuery({
    queryKey: ['nodes'],
    queryFn: async () => {
      const response = await getNodes();
      return response.data;
    },
  });

  const { refetch: refetchVMs } = useQuery({
    queryKey: ['vms'],
    queryFn: async () => {
      const response = await getVMs();
      return response.data;
    },
    enabled: false, // Only fetch when we explicitly call refetch
  });

  const { data: descMap = {} } = useQuery({
    queryKey: ['image-descriptions'],
    queryFn: async () => (await getImageDescriptions()).data,
  });

  const createMutation = useMutation({
    mutationFn: createVM,
    onSuccess: async () => {
      // Start polling for the VM
      setIsPolling(true);
      setPollingMessage('VM creation initiated. Checking status...');

      // Poll every 5 seconds for up to 5 minutes
      const pollInterval = 5000; // 5 seconds
      const maxPolls = 60; // 5 minutes
      let pollCount = 0;

      const pollForVM = async () => {
        pollCount++;

        try {
          const result = await refetchVMs();
          const vms = result.data?.items || [];

          // Check if any VM is in pending state or if we have a new VM
          const hasPendingVM = vms.some(vm =>
            vm.status && vm.status.toLowerCase() === 'pending'
          );

          if (hasPendingVM) {
            setPollingMessage('VM is being created (Pending state)...');

            if (pollCount < maxPolls) {
              setTimeout(pollForVM, pollInterval);
            } else {
              // Timeout reached, but VM exists
              setPollingMessage('VM creation is taking longer than expected, but it\'s in progress.');
              setTimeout(() => {
                setIsPolling(false);
                onSuccess();
              }, 2000);
            }
          } else {
            // No pending VMs, assume it's running or ready
            setPollingMessage('VM created successfully!');
            setTimeout(() => {
              setIsPolling(false);
              onSuccess();
            }, 1000);
          }
        } catch (error) {
          console.error('Error polling for VM:', error);
          if (pollCount < maxPolls) {
            setTimeout(pollForVM, pollInterval);
          } else {
            setIsPolling(false);
            onSuccess(); // Close anyway, the VM list will update
          }
        }
      };

      // Start polling after a short delay
      setTimeout(pollForVM, 2000);
    },
    onError: () => {
      setIsPolling(false);
    },
  });

  const nodes = nodesData?.items || [];

  // When a specific node is chosen, warn if the requested CPU/memory exceeds what's
  // free on it. availableMemory comes back as a string like "120Gi" - parse the
  // leading number; skip the check if it can't be parsed (no false alarms).
  const selectedNode = nodes.find((n) => n.name === node);
  let fitWarning = null;
  if (selectedNode) {
    const availCpu = parseInt(selectedNode.availableCpu, 10);
    const availMem = parseFloat(String(selectedNode.availableMemory));
    const reqCpu = parseInt(cpu, 10);
    const reqMem = parseFloat(memory);
    const overCpu = Number.isFinite(availCpu) && Number.isFinite(reqCpu) && reqCpu > availCpu;
    const overMem = Number.isFinite(availMem) && Number.isFinite(reqMem) && reqMem > availMem;
    if (overCpu || overMem) {
      fitWarning = `Node ${selectedNode.name} has ${selectedNode.availableCpu} cores / ${selectedNode.availableMemory} free - your request (${cpu || '?'} cores / ${memory || '?'} GB) won't fit. Reduce CPU/memory or pick another node.`;
    }
  }

  const filteredImages = useMemo(() => {
    const images = imagesData?.items || [];
    let harborImages = images;
    if (showLatestOnly) {
      harborImages = images.filter(img => img.tag === 'latest');
    }

    // Always include OrkaHub images at the top
    return [...ORKAHUB_IMAGES, ...harborImages];
  }, [imagesData, showLatestOnly]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedImage) {
      alert('Please select an image');
      return;
    }

    const payload = {
      image: selectedImage,
    };

    if (cpu) payload.cpu = parseInt(cpu);
    if (memory) payload.memory = parseInt(memory);
    if (name) payload.name = name;
    if (node) payload.node = node;

    createMutation.mutate(payload);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create New VM</h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {createMutation.isError && (
              <div className="error" style={{ marginBottom: '1rem' }}>
                <strong>Error:</strong> {createMutation.error.response?.data?.detail || createMutation.error.message}
              </div>
            )}

            {isPolling && (
              <div style={{
                background: '#065f46',
                border: '1px solid #047857',
                color: '#d1fae5',
                padding: '1rem',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '3px solid #d1fae5',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'orka-spin 1s linear infinite'
                }}></div>
                <span>{pollingMessage}</span>
              </div>
            )}

            <div className="form-group">
              <label>Image *</label>
              <select
                value={selectedImage}
                onChange={(e) => setSelectedImage(e.target.value)}
                required
                disabled={imagesLoading || isPolling}
              >
                <option value="">
                  {imagesLoading ? 'Loading images...' : 'Select an image'}
                </option>

                {/* OrkaHub Images */}
                <optgroup label="OrkaHub Images">
                  {ORKAHUB_IMAGES.map((image, idx) => (
                    <option key={`orkahub-${idx}`} value={image.full_name}>
                      {image.name}
                    </option>
                  ))}
                </optgroup>

                {/* Harbor Images */}
                {filteredImages.filter(img => !img.isOrkaHub).length > 0 && (
                  <optgroup label="Harbor Images">
                    {filteredImages.filter(img => !img.isOrkaHub).map((image, idx) => (
                      <option key={`harbor-${image.name}-${image.tag}-${idx}`} value={image.full_name}>
                        {descMap[image.name] ? `${descMap[image.name]} - ${image.name}:${image.tag}` : `${image.name}:${image.tag}`}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            <div className="checkbox-group" style={{ marginBottom: '1.25rem' }}>
              <input
                type="checkbox"
                id="showLatestOnly"
                checked={showLatestOnly}
                onChange={(e) => setShowLatestOnly(e.target.checked)}
              />
              <label htmlFor="showLatestOnly">Show only "latest" tags</label>
            </div>

            <div className="form-group">
              <label>VM Name (optional)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Leave empty for auto-generated name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vm-cpu">CPU Cores</label>
              <input
                id="vm-cpu"
                type="number"
                value={cpu}
                onChange={(e) => setCpu(e.target.value)}
                placeholder="e.g. 14"
                min="1"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vm-memory">Memory (GB)</label>
              <input
                id="vm-memory"
                type="number"
                value={memory}
                onChange={(e) => setMemory(e.target.value)}
                placeholder="e.g. 60"
                min="2"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vm-node">Node</label>
              <select
                id="vm-node"
                value={node}
                onChange={(e) => setNode(e.target.value)}
                disabled={nodesLoading}
              >
                <option value="">
                  {nodesLoading ? 'Loading nodes...' : 'Auto-select node'}
                </option>
                {nodes.map((n) => (
                  <option key={n.name} value={n.name}>
                    {n.name} · {n.vmCount}/2 VMs · {n.availableCpu} cores · {n.availableMemory} free
                  </option>
                ))}
              </select>
              {fitWarning && (
                <p className="vm-fit-warning" style={{ color: '#f59e0b', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                  ⚠ {fitWarning}
                </p>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={createMutation.isPending || isPolling}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending || isPolling || !selectedImage}
            >
              {createMutation.isPending ? 'Creating...' : isPolling ? 'Checking status...' : 'Create VM'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateVMModal;
